import * as React from "react";
import * as Sentry from "@sentry/react";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Maximize2,
  Minimize2,
  RotateCw,
  Search,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { toast } from "react-toastify";
import { useToggle } from "react-use";
import { useGetFileDetailsQuery } from "@/api/files";
import { downloadBlob, getPdfContent, pdfUrlFor, toPdfFilename } from "@/components/feature/pdf/helpers";
import { Tooltip } from "@/components/ui/tooltip";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";

interface Props {
  numPages: number;
  currentPage: number;
  onJumpToPage: (pageIndex: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitWidth: () => void;
  onRotate: () => void;
  searchOpen: boolean;
  onToggleSearch: (open?: boolean) => void;
  searchText: string;
  onSearch: (text: string) => void;
  matchPages: number[];
  activeMatchIndex: number;
  onJumpToMatch: (matchIndex: number) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  portalContainer?: HTMLElement;
}

const buttonClassName =
  "rounded p-1.5 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900 disabled:opacity-40";

const tooltipClassName = "rounded bg-black text-xs text-white";

export const PdfToolbar = ({
  numPages,
  currentPage,
  onJumpToPage,
  onZoomIn,
  onZoomOut,
  onFitWidth,
  onRotate,
  searchOpen,
  onToggleSearch,
  searchText,
  onSearch,
  matchPages,
  activeMatchIndex,
  onJumpToMatch,
  isFullscreen,
  onToggleFullscreen,
  portalContainer,
}: Props) => {
  const [searchInput, setSearchInput] = React.useState("");
  const [pageInput, setPageInput] = React.useState(String(currentPage + 1));
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const { currentPdfId } = useCurrentPdf();
  const { data: fileDetails } = useGetFileDetailsQuery({ fse_id: currentPdfId || "" }, { skip: !currentPdfId });
  const [downloading, toggleDownloading] = useToggle(false);

  const handleDownload = async () => {
    if (!currentPdfId) return;
    toggleDownloading(true);
    try {
      const response = await getPdfContent(currentPdfId);
      downloadBlob(response.data, toPdfFilename(fileDetails?.name));
    } catch (downloadError) {
      Sentry.captureException(downloadError);
      toast.error("Failed to download document.");
    } finally {
      toggleDownloading(false);
    }
  };

  React.useEffect(() => {
    setPageInput(String(currentPage + 1));
  }, [currentPage]);

  React.useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const submitPageInput = () => {
    const page = Number.parseInt(pageInput, 10);
    if (Number.isFinite(page) && page >= 1 && page <= numPages) {
      onJumpToPage(page - 1);
    } else {
      setPageInput(String(currentPage + 1));
    }
  };

  const closeSearch = () => {
    onToggleSearch(false);
    setSearchInput("");
    onSearch("");
  };

  return (
    <div className="flex flex-col border-b border-gray-200 bg-gray-50">
      <div className="flex items-center gap-1 px-2 py-1">
        <Tooltip text="Search document" side="bottom" className={tooltipClassName} portalContainer={portalContainer}>
          <button
            type="button"
            className={buttonClassName}
            aria-label="Search document"
            onClick={() => onToggleSearch()}
          >
            <Search size={16} />
          </button>
        </Tooltip>
        <div className="mx-2 flex items-center gap-1 text-sm text-gray-700">
          <input
            className="w-12 rounded border border-gray-300 px-1 py-0.5 text-center"
            value={pageInput}
            onChange={event => setPageInput(event.target.value)}
            onBlur={submitPageInput}
            onKeyDown={event => {
              if (event.key === "Enter") submitPageInput();
            }}
            aria-label="Page number"
          />
          <span>/ {numPages || "-"}</span>
        </div>
        <Tooltip text="Previous page" side="bottom" className={tooltipClassName} portalContainer={portalContainer}>
          <button
            type="button"
            className={buttonClassName}
            aria-label="Previous page"
            disabled={currentPage <= 0}
            onClick={() => onJumpToPage(currentPage - 1)}
          >
            <ChevronUp size={16} />
          </button>
        </Tooltip>
        <Tooltip text="Next page" side="bottom" className={tooltipClassName} portalContainer={portalContainer}>
          <button
            type="button"
            className={buttonClassName}
            aria-label="Next page"
            disabled={currentPage >= numPages - 1}
            onClick={() => onJumpToPage(currentPage + 1)}
          >
            <ChevronDown size={16} />
          </button>
        </Tooltip>
        <div className="mx-1 h-4 w-px bg-gray-300" />
        <Tooltip text="Zoom out" side="bottom" className={tooltipClassName} portalContainer={portalContainer}>
          <button type="button" className={buttonClassName} aria-label="Zoom out" onClick={onZoomOut}>
            <ZoomOut size={16} />
          </button>
        </Tooltip>
        <Tooltip text="Zoom in" side="bottom" className={tooltipClassName} portalContainer={portalContainer}>
          <button type="button" className={buttonClassName} aria-label="Zoom in" onClick={onZoomIn}>
            <ZoomIn size={16} />
          </button>
        </Tooltip>
        <button
          type="button"
          className="rounded px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
          onClick={onFitWidth}
        >
          Fit width
        </button>
        <div className="mx-1 h-4 w-px bg-gray-300" />
        <Tooltip text="Rotate clockwise" side="bottom" className={tooltipClassName} portalContainer={portalContainer}>
          <button type="button" className={buttonClassName} aria-label="Rotate clockwise" onClick={onRotate}>
            <RotateCw size={16} />
          </button>
        </Tooltip>
        <div className="flex-1" />
        <Tooltip text="Download document" side="bottom" className={tooltipClassName} portalContainer={portalContainer}>
          <button
            type="button"
            className={buttonClassName}
            aria-label="Download document"
            disabled={!currentPdfId || downloading}
            onClick={() => void handleDownload()}
          >
            <Download size={16} />
          </button>
        </Tooltip>
        <Tooltip text="Open in new tab" side="bottom" className={tooltipClassName} portalContainer={portalContainer}>
          <button
            type="button"
            className={buttonClassName}
            aria-label="Open in new tab"
            disabled={!currentPdfId}
            onClick={() => currentPdfId && window.open(pdfUrlFor(currentPdfId), "_blank", "noopener,noreferrer")}
          >
            <ExternalLink size={16} />
          </button>
        </Tooltip>
        <Tooltip text="Open in this tab" side="bottom" className={tooltipClassName} portalContainer={portalContainer}>
          <button
            type="button"
            className={buttonClassName}
            aria-label="Open in this tab"
            disabled={!currentPdfId}
            onClick={() => currentPdfId && window.location.assign(pdfUrlFor(currentPdfId))}
          >
            <ArrowRight size={16} />
          </button>
        </Tooltip>
        <Tooltip
          text={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          side="bottom"
          className={tooltipClassName}
          portalContainer={portalContainer}
        >
          <button
            type="button"
            className={buttonClassName}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onClick={onToggleFullscreen}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </Tooltip>
      </div>
      {searchOpen && (
        <div className="flex items-center gap-2 border-t border-gray-200 px-2 py-1">
          <input
            ref={searchInputRef}
            className="w-56 rounded border border-gray-300 px-2 py-0.5 text-sm"
            placeholder="Search in document…"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") onSearch(searchInput);
              if (event.key === "Escape") closeSearch();
            }}
            aria-label="Search text"
          />
          <span className="text-xs text-gray-500">
            {searchText
              ? matchPages.length > 0
                ? `Match ${activeMatchIndex + 1} of ${matchPages.length} (pages)`
                : "No matches"
              : "Press Enter to search"}
          </span>
          <button
            type="button"
            className={buttonClassName}
            aria-label="Previous match"
            disabled={matchPages.length === 0}
            onClick={() => onJumpToMatch((activeMatchIndex - 1 + matchPages.length) % matchPages.length)}
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            className={buttonClassName}
            aria-label="Next match"
            disabled={matchPages.length === 0}
            onClick={() => onJumpToMatch((activeMatchIndex + 1) % matchPages.length)}
          >
            <ChevronDown size={14} />
          </button>
          <button type="button" className={buttonClassName} aria-label="Close search" onClick={closeSearch}>
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};
